<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import bootstrap from "bootstrap/dist/js/bootstrap.min.js";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import PlanHeaderActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import CreateManualStopModal from "@/views/stops/components/CreateManualStopModal.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { planStore } from "@/_store/planStore";
import { routeStore } from "@/_store/routeStore";
import { stopStore } from "@/_store/stopStore";
import { taskStore } from "@/_store/taskStore";
import { vehicleStore } from "@/_store/vehicleStore";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";
import { errorUtils } from "@/utils";
import { toSafeString } from "@/utils/strings";
import KanbanBoardRoutes from "./components/KanbanBoardRoutes.vue";
import KanbanRoutesTable from "./components/tabs/KanbanRoutesTable.vue";
import KanbanRoutesStopsTable from "./components/tabs/KanbanRoutesStopsTable.vue";
import KanbanBultosTable from "./components/tabs/KanbanBultosTable.vue";
import KanbanUnassignedPanel from "./components/KanbanUnassignedPanel.vue";
import PlanMapTab from "./components/tabs/PlanMapTab.vue";
import AddRouteVehicleModal from "./components/AddRouteVehicleModal.vue";
import MobileRouteFlow from "./components/MobileRouteFlow.vue";
import { useConfirmModal } from "@/composables/useConfirmModal";
import { usePlannerKanban } from "./composables/usePlannerKanban";
import { getAvailableRouteVehicleOptions } from "./utils/routeVehicleOptions";
import {
  canDeletePlanRoutes,
  canOptimizePlanRoutes,
  canSendPlanToDriver,
  isPlanEditable,
  isPlanFinalized,
  normalizePlanStatus,
  PLAN_STATUS,
} from "./utils/planStatus";

const route = useRoute();
const kanbanTableStore = argonTableStore();
const plannerStore = planStore();
const routesStore = routeStore();
const stopsStore = stopStore();
const tasksStore = taskStore();
const vehiclesStore = vehicleStore();
const {
  setVehicleOptions,
  setUnassignedStops,
  setPlanContext,
  clearPlanContext,
  resolveDropAction,
  clearDragSnapshot,
  restoreDragSnapshot,
  assignedRouteBoards,
  vehicleOptions,
} = usePlannerKanban();
const {
  showConfirmModal,
  confirmTitle,
  confirmText,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass,
  cancelButtonClass,
  confirmLoading,
  openConfirmModal,
  handleConfirmModal,
} = useConfirmModal();
const snackbar = ref(null);
const planInfo = ref(null);
const currentTab = ref("general");
const plannerViewRef = ref(null);
const kanbanScrollRef = ref(null);
let documentClickGuardActive = false;
const isUnassignedCollapsed = ref(false);
const lastSupportDataLoadKey = ref("");
const pendingPlanStatus = ref("");
const isOptimizingRoutes = ref(false);
const isDragDropPending = ref(false);
const isBoardCanvasPending = ref(false);
const isPlanLoading = ref(false);
const showAddRouteVehicleModal = ref(false);
const mobileRouteFlowRef = ref(null);
const mobileRouteFlowVehicleId = ref("");
const plannerTabs = [
  { label: "Panel", value: "general" },
  { label: "Mapa", value: "mapa" },
  { label: "Rutas", value: "rutas" },
  { label: "Paradas", value: "paradas-rutas" },
  { label: "Bultos", value: "bultos" },
];
const sortableScrollOptions = reactive({
  scroll: true,
  scrollSensitivity: 120,
  scrollSpeed: 15,
  forceFallback: true,
  bubbleScroll: false,
});
function onMobileFlowCompleted() {
  const planId = extractPlanId();
  if (!planId) return;
  refreshPlanById(planId).catch(() => {});
  refreshSupportBoards(true).catch(() => {});
}

watch(
  () => kanbanScrollRef.value,
  (value) => {
    if (!value) return;
    sortableScrollOptions.scroll = value;
  },
  { immediate: true }
);

function getOpenPlannerDropdowns() {
  if (!plannerViewRef.value) return [];
  const openMenus = Array.from(
    plannerViewRef.value.querySelectorAll(".dropdown-menu.show")
  );
  return Array.from(
    new Set(openMenus.map((menu) => menu.closest(".dropdown")).filter(Boolean))
  );
}

function onDocumentClickGuard(event) {
  const openDropdowns = getOpenPlannerDropdowns();
  if (openDropdowns.length === 0) {
    removeDocumentClickGuard();
    return;
  }
  const isInsideDropdown = openDropdowns.some((d) => d.contains(event.target));
  if (!isInsideDropdown) {
    closeOpenPlannerDropdowns();
  }
}

function addDocumentClickGuard() {
  if (documentClickGuardActive) return;
  documentClickGuardActive = true;
  document.addEventListener("click", onDocumentClickGuard, true);
}

function removeDocumentClickGuard() {
  if (!documentClickGuardActive) return;
  documentClickGuardActive = false;
  document.removeEventListener("click", onDocumentClickGuard, true);
}

function getDropdownToggle(dropdownRoot) {
  return dropdownRoot?.querySelector("[data-bs-toggle='dropdown']") || null;
}

function hideDropdownRoot(dropdownRoot) {
  if (!dropdownRoot) return;

  const toggle = getDropdownToggle(dropdownRoot);
  const dropdownApi = bootstrap?.Dropdown;
  if (toggle && dropdownApi?.getOrCreateInstance) {
    dropdownApi.getOrCreateInstance(toggle).hide();
    return;
  }

  dropdownRoot.classList.remove("show");
  dropdownRoot.querySelector(".dropdown-menu")?.classList.remove("show");
  toggle?.setAttribute("aria-expanded", "false");
}

function closeOpenPlannerDropdowns(excludedDropdown = null) {
  getOpenPlannerDropdowns().forEach((dropdownRoot) => {
    if (excludedDropdown && dropdownRoot === excludedDropdown) return;
    hideDropdownRoot(dropdownRoot);
  });
}

function onPlannerDropdownShown(event) {
  const eventTargetDropdown =
    event?.target?.classList?.contains("dropdown")
      ? event.target
      : event?.target?.closest?.(".dropdown");
  const currentDropdown =
    event?.relatedTarget?.closest?.(".dropdown") || eventTargetDropdown || null;

  closeOpenPlannerDropdowns(currentDropdown);
  // The document capture guard is only needed on the board tab, where kanban cards
  // have @click.stop that would otherwise block Bootstrap's clearMenus (bubble phase).
  // Grid tabs (rutas, paradas-rutas, bultos) rely on Bootstrap's native clearMenus.
  if (currentTab.value === "general") {
    addDocumentClickGuard();
  }
}

function onPlannerDropdownHidden() {
  if (getOpenPlannerDropdowns().length === 0) {
    removeDocumentClickGuard();
  }
}

function bindPlannerDropdownGuard() {
  if (!plannerViewRef.value) return;
  plannerViewRef.value.addEventListener("shown.bs.dropdown", onPlannerDropdownShown);
  plannerViewRef.value.addEventListener("hidden.bs.dropdown", onPlannerDropdownHidden);
}

function unbindPlannerDropdownGuard() {
  if (!plannerViewRef.value) return;
  plannerViewRef.value.removeEventListener("shown.bs.dropdown", onPlannerDropdownShown);
  plannerViewRef.value.removeEventListener("hidden.bs.dropdown", onPlannerDropdownHidden);
  removeDocumentClickGuard();
}

function closeSnackbar() {
  snackbar.value = null;
}

function showSnackbar({
  title = "Error",
  message = "",
  type = "danger",
  color = "white",
}) {
  snackbar.value = {
    title,
    message,
    type,
    color,
  };
}

function toSafeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeStatusKey(value) {
  return normalizePlanStatus(value);
}

function getPlanStatusInitial(status) {
  const normalizedStatus = normalizeStatusKey(status);
  const label =
    ESTATUS_ENUM.plan?.[normalizedStatus]?.label || normalizedStatus;
  return toSafeString(label).charAt(0).toUpperCase() || "?";
}

const normalizedPlanStatus = computed(() =>
  normalizeStatusKey(planInfo.value?.status)
);
const isFinalizedPlan = computed(() => isPlanFinalized(normalizedPlanStatus.value));
const isEditablePlan = computed(() => isPlanEditable(normalizedPlanStatus.value));
const canOptimizeRoutes = computed(() =>
  canOptimizePlanRoutes(normalizedPlanStatus.value)
);
const canSendToDriver = computed(() =>
  canSendPlanToDriver(normalizedPlanStatus.value)
);

const planHeaderActions = computed(() => {
  const isFinalized = isFinalizedPlan.value;

  return [
    [
      {
        label: "Optimizar rutas",
        event: "optimizeRoutes",
        disabled: !canOptimizeRoutes.value,
      },
      {
        label: "Añadir vehículo",
        event: "addVehicle",
        disabled: isFinalized || availableRouteVehicleOptions.value.length === 0,
      },
      {
        label: "En ruta",
        event: "sendToDriver",
        disabled: !canSendToDriver.value,
      },
      {
        label: "Finalizar",
        event: "finalizePlan",
        disabled: isFinalized,
      },
    ],
  ];
});

function getTodayDate() {
  return new Date().toISOString().split("T")[0];
}

function extractPlanId() {
  const value = route?.params?.plan_id;
  return value ? String(value) : "";
}

function extractPlanInfo(planByIdValue) {
  const source = planByIdValue?.data || planByIdValue || {};
  if (!source || typeof source !== "object") return null;
  if (source?.plan && typeof source.plan === "object") return source.plan;
  if (
    source?.data &&
    typeof source.data === "object" &&
    !Array.isArray(source.data)
  )
    return source.data;
  return source;
}

function extractUnassignedFromResponse(response) {
  const source = response?.data || response || {};

  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.stops)) return source.stops;
  if (Array.isArray(source?.unassigned)) return source.unassigned;
  if (Array.isArray(source?.items)) return source.items;
  if (Array.isArray(source?.data?.stops)) return source.data.stops;
  if (Array.isArray(source?.data?.unassigned)) return source.data.unassigned;
  if (Array.isArray(source?.data?.items)) return source.data.items;

  return [];
}

function extractPlanDate(planInfo) {
  const source = planInfo || {};
  if (source?.date === null || source?.date === undefined) return "";
  return String(source.date);
}

function formatPlanDateLabel(value) {
  const normalized = (value || "").split("T")[0];
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return normalized || "-";
  return `${match[3]}-${match[2]}-${match[1]}`;
}

function normalizeVehicleOption(vehicle, index) {
  const value =
    vehicle?._id ||
    vehicle?.id ||
    vehicle?.vehicle_id ||
    `vehicle_${index}`;
  const label =
    vehicle?.name ||
    vehicle?.label ||
    vehicle?.plate ||
    "Vehiculo";

  return {
    value,
    label,
    abbreviation: vehicle?.abbreviation || "",
    color: vehicle?.route_color_hex || vehicle?.color || "",
  };
}

async function refreshVehicleOptionsForDate(date) {
  if (!isEditablePlan.value) {
    setVehicleOptions([]);
    return;
  }

  const normalizedDate = toSafeString(date);
  if (!normalizedDate) {
    setVehicleOptions([]);
    return;
  }

  await vehiclesStore.getActiveVehicles(normalizedDate);
  const vehicles = toSafeArray(vehiclesStore.activeVehicles);
  const normalizedOptions = vehicles
    .map((vehicle, index) => normalizeVehicleOption(vehicle, index))
    .filter((vehicle) => Boolean(vehicle.value));
  setVehicleOptions(normalizedOptions);
}

async function refreshUnassignedStopsBoard({ date = localValue.date } = {}) {
  const normalizedDate = toSafeString(date);
  const normalizedType = toSafeString(planInfo.value?.type || "AMBOS").toUpperCase();
  if (!isEditablePlan.value) {
    setUnassignedStops([]);
    return [];
  }
  if (!normalizedDate) {
    setUnassignedStops([]);
    return [];
  }

  const response = await stopsStore.getUnassignedStopsForKanban(
    normalizedDate,
    normalizedType
  );
  const stops = extractUnassignedFromResponse(response);
  setUnassignedStops(stops);
  return stops;
}

function mergePlanInfoFromResponse(response) {
  const normalizedPlanInfo = extractPlanInfo(response);
  if (
    !normalizedPlanInfo ||
    typeof normalizedPlanInfo !== "object" ||
    Array.isArray(normalizedPlanInfo)
  )
    return;

  planInfo.value = {
    ...(planInfo.value || {}),
    ...normalizedPlanInfo,
  }; 

}

async function applyRouteResponse(response, { refreshUnassigned = true } = {}) {
  setPlanContext(response);
  mergePlanInfoFromResponse(response);
  if (refreshUnassigned) {
    await refreshUnassignedStopsBoard();
  }
}

async function refreshPlanById(planId) {
  if (!planId) {
    clearPlanContext();
    planInfo.value = null;
    return;
  }

  await plannerStore.getPlanByIdForKanban(planId);
}

async function refreshSupportBoards(force = false) {
  const planId = extractPlanId();
  const date = toSafeString(localValue.date);
  const type = toSafeString(planInfo.value?.type || "AMBOS").toUpperCase();
  const loadKey = `${planId}|${date}|${type}|${isEditablePlan.value ? "editable" : "closed"}`;
  if (!force && lastSupportDataLoadKey.value === loadKey) return;

  if (!planId || !date) {
    setVehicleOptions([]);
    setUnassignedStops([]);
    lastSupportDataLoadKey.value = loadKey;
    return;
  }

  const results = await Promise.allSettled([
    isEditablePlan.value
      ? refreshVehicleOptionsForDate(date)
      : Promise.resolve(),
    isEditablePlan.value
      ? refreshUnassignedStopsBoard({ date })
      : Promise.resolve([]),
  ]);
  const rejected = results.find((result) => result.status === "rejected");
  if (rejected) throw rejected.reason;

  lastSupportDataLoadKey.value = loadKey;
}

async function executeDropAction(planId, dropAction) {
  const actionType = dropAction?.type || "";
  const payload = dropAction?.payload || {};

  if (actionType === "assign-unassigned-stop") {
    const response = await plannerStore.assignUnassignedStopToRouteForKanban(
      planId,
      payload.routeId,
      payload.stopIds,
      payload.newOrder
    );
    await applyRouteResponse(response, { refreshUnassigned: true });
    return;
  }

  if (actionType === "unassign-route-stop") {
    const response = await plannerStore.unassignRouteStopForKanban(
      planId,
      payload.routeId,
      payload.routeStopIds
    );
    await applyRouteResponse(response, { refreshUnassigned: true });
    return;
  }

  if (actionType === "move-route-stop") {
    await handleMoveRouteStop(planId, payload);
    return;
  }

  if (actionType === "reorder-route-stop") {
    const response = await plannerStore.reorderRouteStopForKanban(
      planId,
      payload.routeId,
      payload.routeStopIds,
      payload.newOrder
    );
    await applyRouteResponse(response, { refreshUnassigned: false });
    return;
  }

  clearDragSnapshot();
}

async function handleAssignVehicle(planId, payload) {
  const vehicleId = payload?.vehicleId || "";
  if (!vehicleId) throw new Error("Debe seleccionar un vehículo válido.");

  isBoardCanvasPending.value = true;
  try {
    const response = await plannerStore.assignVehicleToRouteForKanban(
      planId,
      vehicleId
    );
    await applyRouteResponse(response, { refreshUnassigned: true });
  } finally {
    isBoardCanvasPending.value = false;
  }
}

async function handleMoveRouteStop(planId, payload) {
  const sourceRouteId = payload?.sourceRouteId || "";
  const routeStopIds = Array.isArray(payload?.routeStopIds)
    ? payload.routeStopIds
    : payload?.routeStopId
    ? [payload.routeStopId]
    : [];
  const targetRouteId = payload?.targetRouteId || "";

  if (!sourceRouteId)
    throw new Error("No se encontro route_id de origen para mover la parada.");
  if (!routeStopIds.length)
    throw new Error("No se encontro _id de route_stop para mover la parada.");
  if (!targetRouteId)
    throw new Error("No se encontro route_id de destino para mover la parada.");

  const response = await plannerStore.moveRouteStopToRouteForKanban(
    planId,
    sourceRouteId,
    routeStopIds,
    targetRouteId,
    payload?.newOrder ?? null
  );
  await applyRouteResponse(response, { refreshUnassigned: false });
}

async function handleBulkMoveRouteStops(planId, payload) {
  const group = payload?.groups?.[0];
  const targetRouteId = payload?.targetRouteId || "";
  if (!group || !targetRouteId) throw new Error("No se encontro route_id de destino.");
  if (group.sourceRouteId === targetRouteId) return;
  await handleMoveRouteStop(planId, {
    sourceRouteId: group.sourceRouteId,
    routeStopIds: group.routeStopIds,
    targetRouteId,
    newOrder: null,
  });
}

async function deleteRoutesByIds(planId, routeIds) {
  isBoardCanvasPending.value = true;
  try {
    const routeIdsCsv = Array.isArray(routeIds) ? routeIds.join(",") : routeIds;
    const response = await plannerStore.deleteRouteForKanban(planId, routeIdsCsv);
    await applyRouteResponse(response, { refreshUnassigned: true });
  } finally {
    isBoardCanvasPending.value = false;
  }
}

async function handleCreateUnloadStop(planId, payload) {
  const routeId = payload?.routeId || "";
  if (!routeId)
    throw new Error("No se encontro route_id para crear la parada de descarga.");
  if (!payload?.warehouseId)
    throw new Error("No se encontro warehouse_id para crear la parada de descarga.");

  const taskIds = Array.isArray(payload?.taskIds) ? payload.taskIds : [];
  const stopId = payload?.stopId || null;
  const response = await plannerStore.createUnloadStopForKanban(
    planId,
    routeId,
    payload.warehouseId,
    taskIds,
    payload?.newOrder || 1,
    stopId
  );
  await applyRouteResponse(response, { refreshUnassigned: false });
  showSnackbar({
    title: "OK",
    message: stopId
      ? "Parada de descarga modificada correctamente"
      : "Parada de descarga creada correctamente",
    type: "info",
  });
}

async function handleCreateLoadStop(planId, payload) {
  const routeId = payload?.routeId || "";
  if (!routeId)
    throw new Error("No se encontro route_id para crear la parada de carga.");
  if (!payload?.warehouseId)
    throw new Error("No se encontro warehouse_id para crear la parada de carga.");

  const taskIds = Array.isArray(payload?.taskIds) ? payload.taskIds : [];
  const stopId = payload?.stopId || null;
  const response = await plannerStore.createLoadStopForKanban(
    planId,
    routeId,
    payload.warehouseId,
    taskIds,
    payload?.newOrder || 1,
    stopId
  );
  await applyRouteResponse(response, { refreshUnassigned: false });
  showSnackbar({
    title: "OK",
    message: stopId
      ? "Parada de carga modificada correctamente"
      : "Parada de carga creada correctamente",
    type: "info",
  });
}

async function handleDeleteRoute(planId, payload) {
  const routeId = payload?.routeId || "";
  if (!routeId)
    throw new Error("No se encontro route_id para eliminar la ruta.");

  await deleteRoutesByIds(planId, [routeId]);
}

async function handleDeleteRoutes(planId, payload) {
  const routeIds = Array.isArray(payload?.routeIds)
    ? payload.routeIds.map((routeId) => toSafeString(routeId)).filter(Boolean)
    : [];

  if (routeIds.length === 0) {
    throw new Error("No se encontraron route_id válidos para eliminar las rutas.");
  }

  await deleteRoutesByIds(planId, routeIds);
  showSnackbar({
    title: "OK",
    message:
      routeIds.length === 1
        ? "Ruta eliminada correctamente"
        : "Rutas eliminadas correctamente",
    type: "info",
  });
}

async function handleUpdateRouteStopInstructions(planId, payload) {
  const routeId = payload?.routeId || "";
  const routeStopId = payload?.routeStopId || "";
  if (!routeId || !routeStopId)
    throw new Error("No se encontro la información de la parada para guardar las instrucciones.");

  const response = await plannerStore.updateRouteStopInstructionsForKanban(
    planId,
    routeId,
    routeStopId,
    payload?.instructions || ""
  );
  await applyRouteResponse(response, { refreshUnassigned: false });
}

async function handleUnassignRouteStop(planId, payload) {
  const routeId = payload?.routeId || "";
  const routeStopIds = Array.isArray(payload?.routeStopIds)
    ? payload.routeStopIds.filter(Boolean)
    : typeof payload?.routeStopId === "string" && payload.routeStopId
      ? payload.routeStopId.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
  if (!routeId)
    throw new Error("No se encontro route_id para desasignar la parada.");
  if (!routeStopIds.length)
    throw new Error("No se encontro _id de route_stop para desasignar la parada.");

  const response = await plannerStore.unassignRouteStopForKanban(
    planId,
    routeId,
    routeStopIds
  );
  await applyRouteResponse(response, { refreshUnassigned: true });
}

async function handleDeleteUnassignedStop(planId, payload) {
  void planId;
  const stopId = payload?.stopId || "";
  if (!stopId)
    throw new Error("No se encontro stop_id para eliminar la parada.");

  await stopsStore.deleteStop(stopId);
  if (stopsStore.error) throw stopsStore.error;
  await refreshUnassignedStopsBoard();
  showSnackbar({
    title: "OK",
    message: "Parada eliminada correctamente",
    type: "info",
  });
}

function handleOptimizeRoutes(planId) {
  if (isOptimizingRoutes.value) return;

  openConfirmModal({
    title: "Optimizar rutas",
    text: "Si has realizado cambios manuales en el orden de las paradas, es posible que se pierdan al optimizar la ruta.",
    confirmButtonText: "Optimizar",
    cancelButtonText: "Cancelar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-primary",
    onConfirm: async () => {
      isOptimizingRoutes.value = true;
      plannerStore.optimizePlanRoutesForKanban(planId).catch(() => {});
    },
  });
}

function handleConfirmPlanStatusChange(status) {
  const normalizedStatus = String(status || "").toUpperCase();
  if (!normalizedStatus) return;

  const isFinalizeAction = normalizedStatus === PLAN_STATUS.FINALIZED;
  openConfirmModal({
    title: isFinalizeAction ? "Finalizar plan" : "Enviar plan a conductor",
    text: isFinalizeAction
      ? "El plan se finalizará y las paradas no finalizadan pasarán a no asignadas. Estas seguro de querer finalizarlo?"
      : "Estas seguro de querer poner en ruta el plan?",
    confirmButtonText: "Aceptar",
    cancelButtonText: "Cancelar",
    confirmButtonClass: "btn btn-xs ms-1 bg-gradient-primary",
    onConfirm: () => {
      handleKanbanAction({
        type: "update-plan-status",
        payload: { status: normalizedStatus },
      });
    },
  });
}

function handleUpdatePlanStatus(planId, payload) {
  const status = (payload?.status || "").toUpperCase();
  if (!status)
    throw new Error("No se recibio el estado del plan para actualizar.");
  pendingPlanStatus.value = status;
  plannerStore.updatePlanStatusForKanban(planId, status);
}

async function handleRefreshUnassigned(planId) {
  void planId;
  await refreshUnassignedStopsBoard();
  showSnackbar({
    title: "OK",
    message: "Parada actualizada correctamente",
    type: "info",
  });
}

const showCreateManualStopModal = ref(false);

const planHeaderMenuActions = [
  [{ label: "Crear parada manual", event: "createManualStop" }],
];

function onPlanHeaderMenuAction(action) {
  if (action?.event === "createManualStop") {
    showCreateManualStopModal.value = true;
  }
}

function closeCreateManualStopModal() {
  showCreateManualStopModal.value = false;
}

async function onCreateManualStopSuccess() {
  closeCreateManualStopModal();
  await refreshUnassignedStopsBoard();
  showSnackbar({
    title: "OK",
    message: "Parada creada correctamente",
    type: "info",
  });
}

async function handleDragDrop(planId, payload) {
  const dragEvent = payload?.event;
  let dropAction = null;
  try {
    dropAction = resolveDropAction(dragEvent);
  } catch (error) {
    restoreDragSnapshot();
    throw error;
  }

  if (!dropAction) {
    clearDragSnapshot();
    return;
  }

  try {
    isDragDropPending.value = true;
    await executeDropAction(planId, dropAction);
    clearDragSnapshot();
  } catch (error) {
    restoreDragSnapshot();
    throw error;
  } finally {
    isDragDropPending.value = false;
  }
}

function handleOpenMobileFlow(planId, payload) {
  mobileRouteFlowVehicleId.value = toSafeString(payload?.vehicleId || "");
  mobileRouteFlowRef.value?.open({
    ...payload,
    planId,
    planName: toSafeString(planInfo.value?.name),
  });
}

const kanbanActionHandlers = Object.freeze({
  "assign-vehicle": handleAssignVehicle,
  "move-route-stop": handleMoveRouteStop,
  "bulk-move-route-stops": handleBulkMoveRouteStops,
  "create-unload-stop": handleCreateUnloadStop,
  "create-load-stop": handleCreateLoadStop,
  "delete-route": handleDeleteRoute,
  "delete-routes": handleDeleteRoutes,
  "unassign-route-stop": handleUnassignRouteStop,
  "update-route-stop-instructions": handleUpdateRouteStopInstructions,
  "delete-unassigned-stop": handleDeleteUnassignedStop,
  "optimize-routes": handleOptimizeRoutes,
  "update-plan-status": handleUpdatePlanStatus,
  "refresh-unassigned": handleRefreshUnassigned,
  "drag-drop": handleDragDrop,
  "mobile-flow-open": handleOpenMobileFlow,
});
const finalizedBlockedActionTypes = new Set([
  "assign-vehicle",
  "move-route-stop",
  "bulk-move-route-stops",
  "create-unload-stop",
  "create-load-stop",
  "delete-route",
  "delete-routes",
  "unassign-route-stop",
  "update-route-stop-instructions",
  "delete-unassigned-stop",
  "optimize-routes",
  "refresh-unassigned",
  "drag-drop",
  "update-plan-status",
]);
const pendingOnlyActionTypes = new Set(["delete-route", "delete-routes"]);

async function handleKanbanAction(contract) {
  const type = contract?.type || "";
  const payload = contract?.payload || {};
  if (!type) return;

  const planId = extractPlanId();
  if (!planId) {
    showSnackbar({
      title: "Error",
      message: "No se encontro plan_id en la URL para procesar la acción.",
    });
    return;
  }

  if (finalizedBlockedActionTypes.has(type) && isFinalizedPlan.value) {
    showSnackbar({
      title: "Error",
      message: "No se pueden realizar cambios en un plan finalizado.",
      type: "danger",
    });
    return;
  }

  if (
    pendingOnlyActionTypes.has(type) &&
    !canDeletePlanRoutes(normalizedPlanStatus.value)
  ) {
    showSnackbar({
      title: "Error",
      message: "No se pueden eliminar rutas en planes finalizados.",
      type: "danger",
    });
    return;
  }

  try {
    const handler = kanbanActionHandlers[type];
    if (!handler) return;
    await handler(planId, payload);
  } catch (error) {
    showSnackbar({
      title: "Error",
      message: errorUtils.getErrorMessageHtml(error) || "No se pudo procesar la acción del kanban.",
      type: "danger",
    });
  }
}

const localValue = reactive({
  date: "",
});
const formattedPlanDate = computed(() => formatPlanDateLabel(localValue.date));
const availableRouteVehicleOptions = computed(() =>
  getAvailableRouteVehicleOptions(
    assignedRouteBoards.value,
    vehicleOptions.value
  )
);

function clearKanbanStores({ resetDate = true } = {}) {
  clearPlanContext();
  clearDragSnapshot();
  planInfo.value = null;
  lastSupportDataLoadKey.value = "";
  pendingPlanStatus.value = "";
  isOptimizingRoutes.value = false;
  isDragDropPending.value = false;
  isBoardCanvasPending.value = false;
  isPlanLoading.value = false;
  showAddRouteVehicleModal.value = false;
  mobileRouteFlowRef.value?.close();
  if (resetDate) {
    localValue.date = "";
  }

  setVehicleOptions([]);
  setUnassignedStops([]);
  kanbanTableStore.clear();
  tasksStore.clear();
  stopsStore.clear();
  routesStore.clear();
}

function closeAddRouteVehicleModal() {
  showAddRouteVehicleModal.value = false;
}

function onAddRouteVehicleConfirm(payload) {
  handleKanbanAction({
    type: "assign-vehicle",
    payload: {
      vehicleId: payload?.vehicleId || "",
    },
  });
}

function onPlanHeaderAction(action) {
  const actionEvent = action?.event || "";
  if (!actionEvent) return;
  if (action?.disabled) return;

  if (actionEvent === "addVehicle") {
    showAddRouteVehicleModal.value = true;
    return;
  }

  if (actionEvent === "sendToDriver") {
    if (!canSendToDriver.value) return;
    handleConfirmPlanStatusChange(PLAN_STATUS.IN_PROGRESS);
    return;
  }

  if (actionEvent === "finalizePlan") {
    handleConfirmPlanStatusChange(PLAN_STATUS.FINALIZED);
    return;
  }

  if (actionEvent === "optimizeRoutes") {
    if (!canOptimizeRoutes.value) return;
    handleKanbanAction({
      type: "optimize-routes",
      payload: {},
    });
  }
}

watch(
  currentTab,
  (nextTab, previousTab) => {
    if (nextTab === previousTab) return;
    closeOpenPlannerDropdowns();
    removeDocumentClickGuard();
  }
);

watch(
  () => localValue.date,
  () => {
    if (!planInfo.value) return;
    refreshSupportBoards().catch(() => {});
  },
  { immediate: true }
);

watch(
  () => route?.params?.plan_id,
  (value, previousValue) => {
    const planId = value ? String(value) : "";
    const previousPlanId = previousValue ? String(previousValue) : "";
    if (planId !== previousPlanId) {
      clearKanbanStores();
    }

    if (!planId) {
      localValue.date = getTodayDate();
      return;
    }

    isPlanLoading.value = true;
    refreshPlanById(planId).catch(() => {
      setPlanContext(null);
      setVehicleOptions([]);
      setUnassignedStops([]);
      localValue.date = getTodayDate();
      isPlanLoading.value = false;
    });
  },
  { immediate: true }
);

watch(
  () => plannerStore.planById,
  async (value) => {
    const normalizedPlanInfo = extractPlanInfo(value);
    planInfo.value = normalizedPlanInfo;
    setPlanContext(normalizedPlanInfo);
    isPlanLoading.value = false;
    isUnassignedCollapsed.value = !isEditablePlan.value;

    const planDate = extractPlanDate(normalizedPlanInfo);
    const nextDate = planDate || getTodayDate();
    localValue.date = nextDate;

    await refreshSupportBoards(true).catch(() => {});
  }
);

watch(
  () => plannerStore.planUpdated,
  async (value) => {
    if (!value || !pendingPlanStatus.value) return;

    const status = pendingPlanStatus.value;
    pendingPlanStatus.value = "";

    const planId = extractPlanId();
    if (!planId) return;

    try {
      await refreshPlanById(planId);
      await refreshSupportBoards(true);
      const successMessage =
        status === PLAN_STATUS.FINALIZED
          ? "Plan finalizado correctamente"
          : "Plan enviado a conductor correctamente";
      showSnackbar({
        title: "OK",
        message: successMessage,
        type: "info",
      });
    } catch (error) {
      showSnackbar({
        title: "Error",
        message:
          error?.message ||
          "No se pudo refrescar el plan tras actualizar estado.",
        type: "danger",
      });
    }
  }
);

watch(
  () => plannerStore.kanbanRoutesOptimized,
  async (value) => {
    if (!value || !isOptimizingRoutes.value) return;

    try {
      await applyRouteResponse(value, { refreshUnassigned: true });
    } catch (error) {
      showSnackbar({
        title: "Error",
        message:
          error?.message || "No se pudo aplicar la optimización de rutas.",
        type: "danger",
      });
    } finally {
      isOptimizingRoutes.value = false;
      isBoardCanvasPending.value = false;
    }
  }
);

watch(
  () => plannerStore.error,
  (value) => {
    if (!value) return;
    pendingPlanStatus.value = "";
    isOptimizingRoutes.value = false;
    isBoardCanvasPending.value = false;
    isPlanLoading.value = false;
    showSnackbar({
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    });
  }
);

function onEscapeKey(event) {
  if (event.key !== "Escape") return;
  if (typeof document === "undefined") return;
  document.querySelectorAll(".sortable-selected").forEach((el) => {
    el.classList.remove("sortable-selected");
  });
}

onBeforeUnmount(() => {
  unbindPlannerDropdownGuard();
  document.removeEventListener("keydown", onEscapeKey);
  clearKanbanStores();
  if (typeof document !== "undefined") {
    document.body?.classList?.remove("kanban-drag-no-select");
  }
});

onMounted(() => {
  bindPlannerDropdownGuard();
  document.addEventListener("keydown", onEscapeKey);
  if (kanbanScrollRef.value) {
    sortableScrollOptions.scroll = kanbanScrollRef.value;
  }
});
</script>

<template>
  <div class="container-fluid">
    <div
      ref="plannerViewRef"
      class="card planner-view"
      :class="{
        'is-board-tab': currentTab === 'general',
        'is-map-tab': currentTab === 'mapa',
      }"
    >
      <div class="planner-header">
        <div class="planner-header-context">
          <div class="planner-header-meta">
            <div v-if="planInfo?.status" class="planner-header-status">
              <argon-status
                entity="plan"
                :status="planInfo.status"
                :text="getPlanStatusInitial(planInfo.status)"
                is-mini
              />
            </div>
            <span class="planner-header-date">{{ formattedPlanDate }}</span>
          </div>

          <div v-if="!isFinalizedPlan" class="planner-header-menu-slot">
            <plan-header-actions-dropdown
              menu-id="plan-header-actions"
              :actions="planHeaderMenuActions"
              aria-label="Acciones del plan"
              icon-class="fas fa-ellipsis-h"
              trigger-class="planner-header-menu-button"
              menu-class="planner-header-menu-dropdown"
              @action="onPlanHeaderMenuAction"
            />
          </div>
        </div>

        <div class="planner-header-tabs-shell">
          <div class="planner-header-tabs">
            <tabs-bootstrap
              v-model="currentTab"
              :tabs="plannerTabs"
              size="sm"
              refined
              style-nav="nav-underline"
            />
          </div>
        </div>

        <div class="planner-header-actions">
          <button
            class="ph-btn ph-btn--action"
            :disabled="planHeaderActions[0]?.[0]?.disabled"
            @click="onPlanHeaderAction(planHeaderActions[0]?.[0])"
          >
            <i class="fa fa-magic"></i>
            <span>Optimizar</span>
          </button>
          <button
            class="ph-btn ph-btn--action"
            :disabled="planHeaderActions[0]?.[1]?.disabled"
            @click="onPlanHeaderAction(planHeaderActions[0]?.[1])"
          >
            <i class="fa fa-plus"></i>
            <span>Vehículo</span>
          </button>
          <button
            class="ph-btn ph-btn--action"
            :disabled="planHeaderActions[0]?.[2]?.disabled"
            @click="onPlanHeaderAction(planHeaderActions[0]?.[2])"
          >
            <i class="fa fa-play"></i>
            <span>En ruta</span>
          </button>
          <button
            class="ph-btn ph-btn--action"
            :disabled="planHeaderActions[0]?.[3]?.disabled"
            @click="onPlanHeaderAction(planHeaderActions[0]?.[3])"
          >
            <i class="fa fa-check-circle"></i>
            <span>Finalizar</span>
          </button>
        </div>
      </div>

      <div
        v-show="currentTab === 'general'"
        class="planner-layout row g-0 kanban-detail-layout"
      >
        <div class="planner-main col">
          <div class="planner-tab-content">
            <div class="general-tab">
              <div
                ref="kanbanScrollRef"
                class="kanban-dp-shell kanban-general-surface"
                :class="{
                  'is-loading': isPlanLoading || isOptimizingRoutes || isBoardCanvasPending,
                }"
              >
                <div class="kanban-dp-container">
                  <div
                    v-if="isEditablePlan"
                    class="planner-sidebar planner-sidebar-inline"
                    :class="{ 'is-collapsed': isUnassignedCollapsed }"
                  >
                    <kanban-unassigned-panel
                      :collapsed="isUnassignedCollapsed"
                      :drag-disabled="isDragDropPending"
                      :sortable-options="sortableScrollOptions"
                      @kanban-action="handleKanbanAction"
                      @toggle-collapse="
                        isUnassignedCollapsed = !isUnassignedCollapsed
                      "
                    />
                  </div>

                  <kanban-board-routes
                    :plan-id="String(route?.params?.plan_id || '')"
                    :plan-info="planInfo"
                    :drag-disabled="isDragDropPending"
                    :sortable-options="sortableScrollOptions"
                    @kanban-action="handleKanbanAction"
                  />
                </div>
              </div>
            </div>

            <div
              v-show="!plannerTabs.some((tab) => tab.value === currentTab)"
              class="placeholder-tab"
            >
              <span>Próximamente</span>
            </div>
          </div>
        </div>
      </div>

      <div v-show="currentTab === 'mapa'" class="map-layout">
        <div class="map-layout-body">
          <plan-map-tab
            :plan-id="String(route?.params?.plan_id || '')"
            :is-active="currentTab === 'mapa'"
            :plan-info="planInfo"
          />
        </div>
      </div>

      <div v-show="currentTab === 'rutas'" class="planner-single-tab routes-tab">
        <kanban-routes-table
          :is-active="currentTab === 'rutas'"
          :plan-id="String(route?.params?.plan_id || '')"
          :plan-status="planInfo?.status || ''"
          @kanban-action="handleKanbanAction"
        />
      </div>

      <div v-show="currentTab === 'paradas-rutas'" class="planner-single-tab route-stops-tab">
        <kanban-routes-stops-table
          :is-active="currentTab === 'paradas-rutas'"
          :plan-id="String(route?.params?.plan_id || '')"
          :plan-status="planInfo?.status || ''"
          @kanban-action="handleKanbanAction"
        />
      </div>

      <div v-show="currentTab === 'bultos'" class="planner-single-tab bultos-tab">
        <kanban-bultos-table
          :is-active="currentTab === 'bultos'"
          :plan-id="String(route?.params?.plan_id || '')"
        />
      </div>

      <div
        v-show="!plannerTabs.some((tab) => tab.value === currentTab)"
        class="planner-single-tab placeholder-tab"
      >
                  <span>Próximamente</span>
      </div>
    </div>

    <mobile-route-flow
      ref="mobileRouteFlowRef"
      :plan-id="String(route?.params?.plan_id || '')"
      :plan-name="toSafeString(planInfo?.name || '')"
      :vehicle-id="mobileRouteFlowVehicleId"
      @completed="onMobileFlowCompleted"
    />

    <confirm-modal
      v-model="showConfirmModal"
      :title="confirmTitle"
      :text="confirmText"
      :confirm-button-text="confirmButtonText"
      :cancel-button-text="cancelButtonText"
      :confirm-button-class="confirmButtonClass"
      :cancel-button-class="cancelButtonClass"
      :loading="confirmLoading"
      @confirm="handleConfirmModal"
    />

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>

    <add-route-vehicle-modal
      v-if="showAddRouteVehicleModal"
      modal-id="add-route-vehicle-modal"
      :vehicle-options="availableRouteVehicleOptions"
      :is-submitting="isBoardCanvasPending"
      @close="closeAddRouteVehicleModal"
      @confirm="onAddRouteVehicleConfirm"
    />

    <create-manual-stop-modal
      v-if="showCreateManualStopModal"
      :default-date="localValue.date"
      @close="closeCreateManualStopModal"
      @success="onCreateManualStopSuccess"
    />
  </div>
</template>
<style scoped lang="scss">
@use "./components/planner-surface-shared";

:global(body.kanban-drag-no-select),
:global(body.kanban-drag-no-select *) {
  -webkit-user-select: none !important;
  user-select: none !important;
}

:global(.kanban-dp-item.sortable-selected .stop-route-card) {
  border-color: var(--bs-primary, #5e72e4);
  box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.15),
    0 1px 2px rgba(16, 24, 40, 0.04);
}

.planner-view {
  --planner-canvas-bg: #ffffff;
  --planner-surface: #ffffff;
  --planner-surface-muted: #f8fafc;
  --planner-surface-soft: #f3f6fb;
  --planner-border: #d8e1ee;
  --planner-border-strong: #c8d4e3;
  --planner-text: var(--color-text-primary);
  --planner-text-soft: var(--color-text-secondary);
  --planner-text-muted: #94a3b8;
  --planner-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --planner-shadow-md: 0 10px 30px rgba(15, 23, 42, 0.08);
  --planner-radius-lg: 18px;
  --planner-radius-md: 14px;
  --planner-radius-sm: 10px;
  --planner-board-width: 245px;
  --planner-board-shell-radius: 16px;
  --planner-board-shell-padding: 7px;
  --planner-header-card-radius: 12px;
  --planner-header-card-min-height: 62px;
  --planner-header-card-padding: 1px 24px 2px 6px;
  --planner-header-title-size: 0.80rem;
  --planner-header-title-weight: 500;
  --planner-header-title-line-height: 1.2;
  --planner-chip-font-size: 0.59rem;
  --planner-chip-font-weight: 600;
  --planner-chip-gap: 2px;
  --planner-chip-min-height: 18px;
  --planner-chip-padding-x: 6px;
  --planner-chip-icon-size: 0.53rem;
  --planner-chip-icon-box-size: 18px;
  --planner-control-size: 18px;
  --planner-drop-zone-radius: 10px;
  --planner-drop-zone-padding: 7px;
  --planner-shell-height: calc(100dvh - 120px);
  --planner-content-height: calc(var(--planner-shell-height) - 60px);
  --planner-kanban-min-height: max(
    320px,
    calc(var(--planner-content-height) - 10px)
  );
  --planner-map-min-height: var(--planner-content-height);
  background: var(--planner-canvas-bg);
  border: 1px solid rgba(216, 225, 238, 0.9);
  border-radius: 22px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.07);
  display: flex;
  flex-direction: column;
  height: var(--planner-shell-height);
  min-height: var(--planner-shell-height);
  overflow: hidden;
  position: relative;
}

.planner-view :deep(.kanban-dropdown-open) {
  position: relative;
  z-index: 1031 !important;
}

.planner-view :deep(.kanban-dropdown-open .dropdown-menu.show) {
  z-index: 1040 !important;
}

.planner-view.is-board-tab {
  height: auto;
  overflow: visible;
}

.planner-view.is-map-tab {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.planner-header {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  min-height: 46px;
  padding: 8px 10px 6px;
  position: relative;
}

.planner-header-context {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(216, 225, 238, 0.82);
  border-radius: 13px;
  display: flex;
  flex: 0 0 var(--planner-board-width, 220px);
  gap: 9px;
  justify-content: space-between;
  min-height: 38px;
  padding: 3px 9px;
  width: var(--planner-board-width, 220px);
}

.planner-header-meta {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 7px;
  justify-content: flex-start;
  min-width: 0;
  width: 100%;
}

.planner-header-menu-slot {
  flex: 0 0 auto;
  height: 22px;
  position: relative;
  width: 22px;
}

.planner-header-date {
  color: #111827;
  display: block;
  flex: 0 1 auto;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.1;
  margin-top: 0;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planner-header-status {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  justify-content: center;
}

.planner-header-tabs-shell {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  min-height: 38px;
  min-width: 0;
  padding: 0;
}

.planner-header-tabs {
  flex: 1 1 auto;
  min-width: 0;
}

.planner-header-actions {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(216, 225, 238, 0.82);
  border-radius: 13px;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 3px;
  min-height: 38px;
  padding: 3px 6px;
}

.ph-btn {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.8125rem;
  font-weight: 400;
  gap: 5px;
  height: 28px;
  justify-content: center;
  line-height: 1;
  min-width: 28px;
  padding: 0 7px;
  transition: background-color 140ms ease, color 140ms ease;
}

.ph-btn:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.06);
  color: #374151;
}

.ph-btn:disabled {
  color: #d1d5db;
  cursor: not-allowed;
}

.ph-btn--action {
  color: #4b5563;
  font-weight: 500;
  padding: 0 10px;
}

.ph-btn .fa-play,
.ph-btn .fa-check-circle {
  font-size: 0.9rem;
}

.ph-btn--success:not(:disabled) {
  color: #2f855a;
}

.ph-btn--success:hover:not(:disabled) {
  background: rgba(47, 133, 90, 0.07);
  color: #276749;
}

.planner-layout {
  align-items: stretch;
  background: transparent;
  box-sizing: border-box;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  height: var(--planner-content-height);
  min-height: var(--planner-content-height);
  padding: 0 10px 10px;
  overflow: hidden;
}

.planner-sidebar {
  align-self: stretch;
  flex: 0 0 var(--planner-board-width, 220px);
  margin-right: 6px;
  overflow-x: visible;
  width: var(--planner-board-width, 220px);
}

.planner-sidebar-inline {
  display: flex;
  flex: 0 0 auto;
}

.planner-sidebar.is-collapsed {
  flex-basis: 38px;
  margin-right: 6px;
  width: 38px;
}

.planner-main {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.planner-tab-content {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.planner-header-tabs :deep(.nav) {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(216, 225, 238, 0.82);
  border-radius: 13px;
  margin-bottom: 0 !important;
  min-height: 34px;
  padding: 0 6px;
  width: 100%;
}

.planner-header-tabs :deep(.nav-item) {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
}

.planner-header-tabs :deep(.nav-link) {
  align-items: center;
  display: inline-flex;
  flex: 1 1 auto;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-width: 0;
}

.planner-header-tabs :deep(.nav-link:not(.active)) {
  border-bottom-color: transparent !important;
}

.planner-header-tabs :deep(.nav-link.active) {
  border-bottom-color: var(--bs-primary) !important;
}

.routes-tab,
.route-stops-tab,
.bultos-tab,
.map-tab,
.placeholder-tab {
  height: auto;
  min-height: 0;
}

.routes-tab,
.route-stops-tab,
.bultos-tab {
  display: flex;
  flex: 1 1 auto;
}

.routes-tab > *,
.route-stops-tab > *,
.bultos-tab > * {
  flex: 1 1 auto;
  min-height: 0;
}

.general-tab {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.planner-single-tab,
.map-layout {
  box-sizing: border-box;
  min-height: 0;
  padding: 0 10px 10px;
}

.planner-single-tab {
  overflow: visible;
}

.map-layout {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.map-layout-body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.map-layout-body > * {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.map-tab {
  display: flex;
  flex-direction: column;
  min-height: var(--planner-map-min-height, calc(100dvh - 250px));
}

.map-tab > * {
  flex: 1 1 auto;
  min-height: calc(100dvh - 210px);
}

.planner-sidebar :deep(.kanban-dp-board) {
  align-self: stretch;
  height: auto;
  min-height: var(--planner-kanban-min-height, 320px);
}

.planner-sidebar :deep(.unassigned-board) {
  flex: 1 1 auto;
  max-width: var(--planner-board-width, 220px);
  min-width: var(--planner-board-width, 220px);
  width: var(--planner-board-width, 220px);
}

.planner-sidebar.is-collapsed :deep(.unassigned-board) {
  max-width: 38px;
  min-width: 38px;
  width: 38px;
}

.general-tab :deep(.kanban-dp-shell) {
  height: 100%;
  min-height: var(--planner-kanban-min-height, 320px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(203, 213, 225, 0.55) transparent;
}

.general-tab :deep(.kanban-dp-shell::-webkit-scrollbar) {
  height: 5px;
  width: 5px;
}

.general-tab :deep(.kanban-dp-shell::-webkit-scrollbar-track) {
  background: transparent;
}

.general-tab :deep(.kanban-dp-shell::-webkit-scrollbar-thumb) {
  background: rgba(203, 213, 225, 0.55);
  border-radius: 999px;
}

.general-tab :deep(.kanban-dp-shell::-webkit-scrollbar-thumb:hover) {
  background: rgba(148, 163, 184, 0.75);
}

.general-tab :deep(.kanban-dp-board) {
  height: auto;
  min-height: 100%;
}

.general-tab :deep(.kanban-dp-board) {
  align-self: stretch;
  max-height: none;
}

.general-tab :deep(.kanban-dp-container) {
  height: auto;
  min-height: 100%;
  align-items: stretch;
}

.general-tab :deep(.kanban-dp-drag) {
  height: auto;
  min-height: 120px;
}

.kanban-general-surface {
  position: relative;
}

.kanban-general-surface.is-loading {
}

.kanban-general-surface.is-loading::before {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.01);
  content: "";
  z-index: 19;
}

.kanban-general-surface.is-loading::after {
  position: absolute;
  inset: 0;
  background-image: url("../../assets/img/loading.svg");
  background-position: center 10%;
  background-repeat: no-repeat;
  background-size: 24px 24px;
  content: "";
  z-index: 20;
}

.placeholder-tab {
  align-items: center;
  color: var(--planner-text-soft);
  display: flex;
  font-size: 0.95rem;
  font-weight: 600;
  justify-content: center;
}
</style>
